import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiActivity, FiFileText, FiGlobe, FiSend, FiUser } from "react-icons/fi";
import Select from "react-select";
import Sidebar from "../components/Sidebar";
import API from "../api/api";
import VisaRouteSelectors from "../components/VisaRouteSelectors";
import { saveAvailableSlotsResult } from "./AvailableSlotsPage";
import "./BookingDetailsPage.css";

function getFrontendBridgeUrl() {
  try {
    const origin = typeof window !== "undefined" && window.location?.origin ? window.location.origin : "https://visaappointmentbot.com";
    const protocol = origin.startsWith("https") ? "wss:" : "ws:";
    return `${protocol}//${origin.replace(/^https?:\/\//, "")}/ws/frontend`;
  } catch {
    return "wss://visaappointmentbot.com/ws/frontend";
  }
}

const FORM_STEPS = [
  { id: 1, title: "Booking", subtitle: "Route & personal" },
  { id: 2, title: "Passport", subtitle: "Identity details" },
  { id: 3, title: "Application", subtitle: "Contact & work" },
  { id: 4, title: "Travel", subtitle: "Trip purpose" },
  { id: 5, title: "Stay", subtitle: "Accommodation" },
  { id: 6, title: "Extra", subtitle: "Additional info" },
];

function isSwitzerlandDestination(country) {
  if (!country) return false;
  const iso = String(country.iso_code || "").trim().toUpperCase();
  if (iso === "CHE" || iso === "CH") return true;
  const name = String(country.name || "").trim().toLowerCase();
  return name === "switzerland" || name.includes("switzerland");
}

const createInitialApplicant = () => ({
  applicationCentre: "",
  appointmentCategory: "",
  subCategory: "",
  firstName: "",
  lastName: "",
  gender: "",
  dateOfBirth: "",
  nationality: "",
  passportNumber: "",
  passportExpiry: "",
  phoneCode: "44",
  contactNumber: "",
  email: "",
  addressLine1: "",
  addressLine2: "",
  surnameAtBirth: "",
  countryOfBirth: "",
  placeOfBirth: "",
  nationalityAtBirth: "",
  otherNationality: "",
  maritalStatus: "",
  applicantIdNumber: "",
  passportType: "",
  passportNumberConfirmation: "",
  passportIssueDate: "",
  passportIssuedCountry: "",
  passportIssuedBy: "",
  applicationHomeAddress: "",
  applicationStreet: "",
  applicationCity: "",
  applicantCountry: "",
  applicantPostalCode: "",
  hasEuFamilyMember: "No",
  currentOccupation: "",
  applicationEmail: "",
  applicationPhoneCode: "",
  applicationTelephone: "",
  residenceOtherCountry: "No",
  fingerprintsCollectedPreviously: "No",
  employerName: "",
  employerAddress: "",
  employerTelephone: "",
  employerCity: "",
  employerPostalCode: "",
  employerCountry: "",
  purposeOfTravel: "",
  purposeOfStayDetails: "",
  numberOfEntries: "",
  schengenVisaPastThreeYears: "No",
  travelInsurance: "Yes",
  invitingPartyType: "",
  hotelName: "",
  hotelAddress: "",
  hotelPostalCode: "",
  hotelPostOfficeBox: "",
  hotelPostOfficeBoxNumber: "",
  hotelEmail: "",
  hotelStreet: "",
  hotelCity: "",
  hotelCountry: "",
  hotelPhoneCode: "",
  hotelTelephone: "",
  hotelFax: "",
  hotelContactSurname: "",
  hotelContactFirstName: "",
  hotelContactEmail: "",
  transitVisa: "No",
  entryPermitIssuedBy: "",
  transitValidFrom: "",
  transitValidUntil: "",
  host: "",
  formFillerName: "",
  hostCompanyOrHotel: "",
  hostFullAddress: "",
  hostTelephone: "",
  hostFax: "",
  hostEmail: "",
  schengenArrivalDate: "",
  schengenDepartureDate: "",
  durationOfStay: "",
  costCoveredBy: [],
  costCoveredByOther: "",
  meansOfSupport: [],
  meansOfSupportOther: "",
  schengenStateFirstEntry: "",
  destinationCountries: [],
  costsCoveredByInvitingParty: "Yes",
  costsCoveredBy: "",
});

const STEP_ONE_REQUIRED_FIELDS = [
  "firstName",
  "lastName",
  "gender",
  "dateOfBirth",
  "nationality",
  "passportNumber",
  "passportExpiry",
  "phoneCode",
  "contactNumber",
  "email",
  "addressLine1",
];

const STEP_TWO_REQUIRED_FIELDS = [
  "surnameAtBirth",
  "countryOfBirth",
  "placeOfBirth",
  "nationalityAtBirth",
  "maritalStatus",
  "passportType",
  "passportIssueDate",
  "passportIssuedCountry",
  "passportIssuedBy",
];

const STEP_THREE_REQUIRED_FIELDS = [
  "applicationHomeAddress",
  "applicationStreet",
  "applicationCity",
  "applicantCountry",
  "applicantPostalCode",
  "hasEuFamilyMember",
  "currentOccupation",
  "applicationEmail",
  "applicationPhoneCode",
  "applicationTelephone",
  "residenceOtherCountry",
  "employerName",
  "employerAddress",
  "employerTelephone",
  "employerCity",
  "employerPostalCode",
  "employerCountry",
  "fingerprintsCollectedPreviously",
];

const STEP_FOUR_REQUIRED_FIELDS = [
  "purposeOfTravel",
  "purposeOfStayDetails",
  "numberOfEntries",
  "schengenVisaPastThreeYears",
  "travelInsurance",
];

const STEP_FIVE_REQUIRED_FIELDS = [
  "invitingPartyType",
];

const STEP_FIVE_HOTEL_ONLY_REQUIRED_FIELDS = [
  "hotelName",
  "hotelAddress",
];

const STEP_FIVE_SHARED_ACCOMMODATION_REQUIRED_FIELDS = [
  "hotelPostalCode",
  "hotelEmail",
  "hotelStreet",
  "hotelCity",
  "hotelCountry",
  "hotelPhoneCode",
  "hotelTelephone",
];

const STEP_FIVE_HOTEL_REQUIRED_FIELDS = [
  ...STEP_FIVE_HOTEL_ONLY_REQUIRED_FIELDS,
  ...STEP_FIVE_SHARED_ACCOMMODATION_REQUIRED_FIELDS,
];

function getStepFiveAccommodationRequiredFields(invitingPartyType) {
  const type = String(invitingPartyType || "").trim();
  if (type === "Hotel") {
    return STEP_FIVE_HOTEL_REQUIRED_FIELDS;
  }
  if (type === "Company" || type === "Other") {
    return STEP_FIVE_SHARED_ACCOMMODATION_REQUIRED_FIELDS;
  }
  return [];
}

function showsAccommodationExtraFields(invitingPartyType) {
  const type = String(invitingPartyType || "").trim();
  return type === "Hotel" || type === "Company" || type === "Other";
}

const STEP_SIX_REQUIRED_FIELDS = [
  "transitVisa",
  "schengenArrivalDate",
  "schengenDepartureDate",
  "durationOfStay",
  "costCoveredBy",
  "meansOfSupport",
  "schengenStateFirstEntry",
  "destinationCountries",
  "costsCoveredByInvitingParty",
];

const STEP_SIX_TRANSIT_REQUIRED_FIELDS = [
  "entryPermitIssuedBy",
  "transitValidFrom",
  "transitValidUntil",
];

const FIELD_LABELS = {
  firstName: "First Name",
  lastName: "Last Name",
  gender: "Gender",
  dateOfBirth: "Date of Birth",
  nationality: "Nationality",
  passportNumber: "Passport Number",
  passportExpiry: "Passport Expiry",
  phoneCode: "Phone Code",
  contactNumber: "Contact Number",
  email: "Email",
  addressLine1: "Address Line 1",
  surnameAtBirth: "Surname at Birth",
  countryOfBirth: "Country of Birth",
  placeOfBirth: "Place of Birth",
  nationalityAtBirth: "Nationality at Birth",
  maritalStatus: "Marital Status",
  passportType: "Type of Passport",
  passportIssueDate: "Passport Date of Issue",
  passportIssuedCountry: "Issued Country",
  passportIssuedBy: "Issued By",
  applicationHomeAddress: "Applicant Home Address",
  applicationStreet: "Application Street",
  applicationCity: "Application City",
  applicantCountry: "Applicant Country",
  applicantPostalCode: "Applicant’s Postal Code",
  hasEuFamilyMember: "EU / EEA / CH / UK Family Member",
  currentOccupation: "Current Occupation",
  applicationEmail: "Applicant Email Address",
  applicationPhoneCode: "Applicant Telephone Code",
  applicationTelephone: "Applicant Telephone",
  residenceOtherCountry: "Residence Outside Current Nationality",
  employerName: "Name of Employer/Education establishment",
  employerAddress: "Employer/Education establishment Address",
  employerTelephone: "Employer/Education establishment Telephone number",
  employerCity: "Employer/Education establishment City",
  employerPostalCode: "Employer/Education establishment Postal Code",
  employerCountry: "Employer/Education establishment Country",
  fingerprintsCollectedPreviously: "Fingerprints Collected Previously",
  purposeOfTravel: "Purpose Of Travel",
  purposeOfStayDetails: "Additional information on purpose of stay",
  numberOfEntries: "Number Of Entries Requested",
  schengenVisaPastThreeYears: "Schengen visas in the past three years",
  travelInsurance: "Travel Insurance",
  invitingPartyType: "Type of inviting party",
  hotelName: "Name of the inviting hotel",
  hotelAddress: "Address of the inviting Hotel",
  hotelPostalCode: "Hotel Postal Code",
  hotelPostOfficeBox: "Post Office Box",
  hotelPostOfficeBoxNumber: "Post Office Box Number",
  hotelEmail: "Hotel Email",
  hotelStreet: "Hotel Street",
  hotelCity: "Hotel City",
  hotelCountry: "Hotel Country",
  hotelPhoneCode: "Hotel Telephone Code",
  hotelTelephone: "Hotel Telephone Number",
  hotelFax: "Fax Number",
  hotelContactSurname: "Surname of the contact person in the inviting hotel",
  hotelContactFirstName: "Firstname(-s) of the contact person in the inviting hotel",
  hotelContactEmail: "Hotel contact email address",
  transitVisa: "Transit Visa?",
  entryPermitIssuedBy: "Entry permit for the final country of dest., issued by",
  transitValidFrom: "Valid from",
  transitValidUntil: "Valid until",
  host: "Host",
  formFillerName: "Name of the Person filling the form instead of applicant",
  hostCompanyOrHotel: "Name Of Host Company Or Hotel",
  hostFullAddress: "Host Full Address",
  hostTelephone: "Host Telephone",
  hostFax: "Host Fax",
  hostEmail: "Host Email Address",
  schengenArrivalDate: "Date Of Arrival in Schengen Area",
  schengenDepartureDate: "Date of Departure from Schengen Area",
  durationOfStay: "Duration Of Stay",
  costCoveredBy: "Cost of travelling and living covered by",
  costCoveredByOther: "Cost of travelling and living covered-Other",
  meansOfSupport: "Applicant's Means of support?",
  meansOfSupportOther: "Applicant's Means of support-Other",
  schengenStateFirstEntry: "Schengen State of First Entry",
  destinationCountries: "Country (-ies) of destination",
  costsCoveredByInvitingParty: "Costs are covered by the inviting person or company",
  costsCoveredBy: "Costs are covered by",
};

const MARITAL_STATUS_OPTIONS = ["Single", "Married", "Divorced", "Widowed", "Separated"].map(
  (value) => ({ value, label: value })
);

const PASSPORT_TYPE_OPTIONS = [
  "Ordinary Passport",
  "Diplomatic Passport",
  "Official Passport",
  "Service Passport",
].map((value) => ({ value, label: value }));

const OCCUPATION_OPTIONS = [
  "Administrative technical and service staff",
  "Agriculture sales section manager",
  "Architect",
  "Artisian",
  "Artist",
  "Banker",
  "Blue Caller Worker",
  "Chauffeurlorry Driver",
  "ChemistChemical Engineer",
  "Civil Servent",
  "Clergyman Religious Figure",
  "Company Executive",
  "Computer Expert",
  "Diplomat",
  "DiplomatsPrivate Servent",
  "Electronics Expert",
  "Farmer",
  "Fashion Cosmetics",
  "Journalist",
  "Legal Professional",
  "Legal Professional (Lawyer), Legal Advisor",
  "Magistrate",
  "Manager",
  "Manger Operations",
  "Medical and Paramedical profession",
  "No Occupation",
  "Not Applicable",
  "Other Kind of Technician",
  "Others",
  "Pensioner",
  "Policeman Soldier",
  "Politician",
  "Professional SportPerson",
  "Scientific Resercher",
  "Seaman",
  "Self employed",
  "Student trainee",
  "Teacher",
  "Tradesman",
  "WhiteColler Worker",
].map((value) => ({ value, label: value }));

const NO_ONLY_OPTIONS = [{ value: "No", label: "No" }];

const YES_NO_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

const PURPOSE_OF_TRAVEL_OPTIONS = [
  "Tourism",
  "Business",
  "Visiting family or friends",
  "Cultural",
  "Sports",
  "Official visit",
  "Medical reasons",
  "Study",
  "Transit",
  "Airport transit",
  "Other",
].map((value) => ({ value, label: value }));

const NUMBER_OF_ENTRIES_OPTIONS = [
  "Single entry",
  "Two entries",
  "Multiple entries",
].map((value) => ({ value, label: value }));

const INVITING_PARTY_TYPE_OPTIONS = [
  "Hotel",
  "Private Person",
  "Company",
  "Other",
].map((value) => ({ value, label: value }));

const COST_COVERED_BY_OPTIONS = [
  "By the applicant himself / herself",
  "By a host",
  "By Others",
].map((value) => ({ value, label: value }));

const MEANS_OF_SUPPORT_OPTIONS = [
  "Accommodation Provided",
  "All Expenses Covered During The Stay",
  "Cash",
  "Credit card",
  "Others",
  "Pre-Paid Transportation",
  "Prepaid accommodation",
  "Traveler's cheques",
].map((value) => ({ value, label: value }));

// Exact VFS Additional Details options for "Entry permit for the final country of dest., issued by".
const ENTRY_PERMIT_ISSUED_BY_OPTIONS = [
  "Austria",
  "Belgium",
  "Bulgaria",
  "Croatia",
  "Cyprus",
  "Czech Republic",
  "Denmark",
  "Estonia",
  "Finland",
  "France",
  "Germany",
  "Greece",
  "Hungary",
  "Iceland",
  "Ireland",
  "Italy",
  "Latvia",
  "Liechtenstein",
  "Lithuania",
  "Luxembourg",
  "Malta",
  "Netherlands",
  "Norway",
  "Poland",
  "Portugal",
  "Romania",
  "Slovakia",
  "Slovenia",
  "Spain",
  "Sweden",
  "Switzerland",
].map((value) => ({ value, label: value }));

const SCHENGEN_DESTINATION_OPTIONS = ENTRY_PERMIT_ISSUED_BY_OPTIONS;

// Quick send from step 1 fills backend-required passport/application fields from step 1 values.
// Full path (steps 2–3) sends the user's real passport + application answers instead.
function withQuickSendPassportAndApplicationFields(applicant) {
  const nationality = String(applicant.nationality || "").trim();
  return {
    ...applicant,
    passportNumberConfirmation: applicant.passportNumber,
    surnameAtBirth: applicant.surnameAtBirth || applicant.lastName,
    countryOfBirth: applicant.countryOfBirth || nationality,
    placeOfBirth: applicant.placeOfBirth || nationality,
    nationalityAtBirth: applicant.nationalityAtBirth || nationality,
    maritalStatus: applicant.maritalStatus || "Single",
    passportType: applicant.passportType || "Ordinary Passport",
    passportIssueDate: applicant.passportIssueDate || applicant.dateOfBirth,
    passportIssuedCountry: applicant.passportIssuedCountry || nationality,
    passportIssuedBy: applicant.passportIssuedBy || nationality,
    applicationHomeAddress: applicant.applicationHomeAddress || applicant.addressLine1,
    applicationStreet: applicant.applicationStreet || applicant.addressLine2 || applicant.addressLine1,
    applicationCity: applicant.applicationCity || nationality,
    applicantCountry: applicant.applicantCountry || nationality,
    applicantPostalCode: applicant.applicantPostalCode || "00000",
    hasEuFamilyMember: applicant.hasEuFamilyMember || "No",
    currentOccupation: applicant.currentOccupation || "Others",
    applicationEmail: applicant.applicationEmail || applicant.email,
    applicationPhoneCode: applicant.applicationPhoneCode || applicant.phoneCode,
    applicationTelephone: applicant.applicationTelephone || applicant.contactNumber,
    residenceOtherCountry: applicant.residenceOtherCountry || "No",
    employerName: applicant.employerName || applicant.lastName || "OTHERS",
    employerAddress: applicant.employerAddress || applicant.addressLine1,
    employerTelephone: applicant.employerTelephone || applicant.contactNumber,
    employerCity: applicant.employerCity || applicant.applicationCity || nationality,
    employerPostalCode: applicant.employerPostalCode || applicant.applicantPostalCode || "00000",
    employerCountry: applicant.employerCountry || applicant.applicantCountry || nationality,
    fingerprintsCollectedPreviously: applicant.fingerprintsCollectedPreviously || "No",
    purposeOfTravel: applicant.purposeOfTravel || "Tourism",
    purposeOfStayDetails: applicant.purposeOfStayDetails || "TOURISM",
    numberOfEntries: applicant.numberOfEntries || "Single entry",
    schengenVisaPastThreeYears: applicant.schengenVisaPastThreeYears || "No",
    travelInsurance: applicant.travelInsurance || "Yes",
    invitingPartyType: applicant.invitingPartyType || "Hotel",
    hotelName: applicant.hotelName || "HOTEL",
    hotelAddress: applicant.hotelAddress || applicant.addressLine1,
    hotelPostalCode: applicant.hotelPostalCode || applicant.applicantPostalCode || "00000",
    hotelEmail: applicant.hotelEmail || applicant.email,
    hotelStreet: applicant.hotelStreet || applicant.addressLine2 || applicant.addressLine1,
    hotelCity: applicant.hotelCity || applicant.applicationCity || nationality,
    hotelCountry: applicant.hotelCountry || applicant.applicantCountry || nationality,
    hotelPhoneCode: applicant.hotelPhoneCode || applicant.phoneCode,
    hotelTelephone: applicant.hotelTelephone || applicant.contactNumber,
    transitVisa: applicant.transitVisa || "No",
    host: applicant.host || applicant.lastName || "HOST",
    formFillerName: applicant.formFillerName || `${applicant.firstName || ""} ${applicant.lastName || ""}`.trim(),
    hostCompanyOrHotel: applicant.hostCompanyOrHotel || applicant.hotelName || "HOTEL",
    hostFullAddress: applicant.hostFullAddress || applicant.addressLine1,
    hostTelephone: applicant.hostTelephone || applicant.contactNumber,
    hostEmail: applicant.hostEmail || applicant.email,
    schengenArrivalDate: applicant.schengenArrivalDate || "2026-10-01",
    schengenDepartureDate: applicant.schengenDepartureDate || "2026-10-07",
    durationOfStay: applicant.durationOfStay || "6",
    costCoveredBy:
      Array.isArray(applicant.costCoveredBy) && applicant.costCoveredBy.length
        ? applicant.costCoveredBy
        : ["By the applicant himself / herself"],
    meansOfSupport:
      Array.isArray(applicant.meansOfSupport) && applicant.meansOfSupport.length
        ? applicant.meansOfSupport
        : ["Cash"],
    schengenStateFirstEntry: applicant.schengenStateFirstEntry || "Austria",
    destinationCountries:
      Array.isArray(applicant.destinationCountries) && applicant.destinationCountries.length
        ? applicant.destinationCountries
        : ["Austria"],
    costsCoveredByInvitingParty: applicant.costsCoveredByInvitingParty || "Yes",
    costsCoveredBy: applicant.costsCoveredBy || "",
  };
}

export default function BookingDetailsPage() {
  const navigate = useNavigate();
  const [originCountry, setOriginCountry] = useState(null);
  const [destinationCountry, setDestinationCountry] = useState(null);
  const [applicationCentre, setApplicationCentre] = useState(null);
  const [appointmentCategory, setAppointmentCategory] = useState(null);
  const [subCategory, setSubCategory] = useState(null);
  const [applicant, setApplicant] = useState(createInitialApplicant);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [job, setJob] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [toast, setToast] = useState(null);
  const [nationalities, setNationalities] = useState([]);
  const [nationalitiesLoading, setNationalitiesLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const toastTimerRef = useRef(null);
  const noAppointmentToastShownRef = useRef(false);

  const nationalityOptions = useMemo(
    () =>
      nationalities.map((country) => ({
        value: String(country.name || "").toUpperCase(),
        label: String(country.name || "").toUpperCase(),
      })),
    [nationalities]
  );

  const selectedNationalityOption = useMemo(() => {
    const nationality = String(applicant.nationality || "").toUpperCase();
    if (!nationality) return null;
    return (
      nationalityOptions.find((option) => option.value === nationality) || {
        value: nationality,
        label: nationality,
      }
    );
  }, [applicant.nationality, nationalityOptions]);

  const getApplicantOption = (field, options = nationalityOptions) => {
    const value = String(applicant[field] || "");
    if (!value) return null;
    return (
      options.find((option) => option.value.toLowerCase() === value.toLowerCase()) || {
        value,
        label: value,
      }
    );
  };

  const getApplicantMultiOptions = (field, options = []) => {
    const raw = applicant[field];
    const values = Array.isArray(raw)
      ? raw.map((item) => String(item || "").trim()).filter(Boolean)
      : String(raw || "")
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);

    return values.map(
      (value) =>
        options.find((option) => option.value.toLowerCase() === value.toLowerCase()) || {
          value,
          label: value,
        }
    );
  };

  const setApplicantSelectField = (field, uppercase = false) => (option) => {
    const value = String(option?.value || "");
    setApplicant((prev) => ({
      ...prev,
      [field]: uppercase ? value.toUpperCase() : value,
    }));
  };

  const setApplicantMultiSelectField = (field, uppercase = false) => (selected) => {
    const values = (selected || []).map((option) => {
      const value = String(option?.value || "");
      return uppercase ? value.toUpperCase() : value;
    });
    setApplicant((prev) => ({
      ...prev,
      [field]: values,
    }));
  };

  const isApplicantFieldFilled = (data, key) => {
    const value = data[key];
    if (Array.isArray(value)) {
      return value.some((item) => String(item || "").trim());
    }
    return Boolean(String(value || "").trim());
  };

  useEffect(() => {
    let active = true;
    setNationalitiesLoading(true);

    API.get("/countries")
      .then((response) => {
        if (active) {
          setNationalities(Array.isArray(response.data) ? response.data : []);
        }
      })
      .catch(() => {
        if (active) setNationalities([]);
      })
      .finally(() => {
        if (active) setNationalitiesLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const showNoAppointmentToast = (message) => {
    const text = String(message || "").trim() || "No appointment slots are currently available. Please try again later.";
    noAppointmentToastShownRef.current = true;
    setToast({
      variant: "danger",
      title: "No Appointment Available",
      message: text,
    });
    try {
      sessionStorage.setItem("vfs_no_appointment_toast", text);
    } catch (_error) {
      // Ignore storage failures.
    }
  };

  const showWaitlistSuccessToast = (message) => {
    const text = String(message || "").trim() || "Waitlist request submitted successfully.";
    setToast({
      variant: "success",
      title: "Waitlist Confirmed",
      message: text,
    });
    try {
      sessionStorage.setItem("vfs_waitlist_success_toast", text);
    } catch (_error) {
      // Ignore storage failures.
    }
  };

  const showBookingFormSuccessToast = (message) => {
    const text = String(message || "").trim() || "Online visa form submitted successfully.";
    setToast({
      variant: "success",
      title: "Form Submitted",
      message: text,
    });
    try {
      sessionStorage.setItem("vfs_booking_form_success_toast", text);
    } catch (_error) {
      // Ignore storage failures.
    }
  };

  const openAvailableSlotsPage = useCallback((payload = {}) => {
    const slotRows = Array.isArray(payload.slotRows) ? payload.slotRows : [];
    const slots = Array.isArray(payload.slots) ? payload.slots : slotRows.map((row) => row.date);
    const saved = saveAvailableSlotsResult({
      jobId: payload.jobId ?? job?.jobId ?? null,
      message: payload.message || "",
      locationLabel: payload.locationLabel || "",
      slotRows,
      slots,
      months: Array.isArray(payload.months) ? payload.months : [],
      originCode: payload.originCode || job?.originCode || originCountry?.iso_code || "",
      destCode: payload.destCode || job?.destinationCode || destinationCountry?.iso_code || "",
      applicationCentre:
        payload.applicationCentre || applicant.applicationCentre || applicationCentre?.centre_name || "",
      appointmentCategory:
        payload.appointmentCategory ||
        applicant.appointmentCategory ||
        appointmentCategory?.category_name ||
        "",
      subCategory: payload.subCategory || applicant.subCategory || subCategory?.sub_category_name || "",
      scannedAt: new Date().toISOString(),
    });

    try {
      sessionStorage.setItem(
        "vfs_available_slots_toast",
        saved.message || `Available slots (${saved.slotRows.length} date(s))`
      );
      sessionStorage.setItem("vfs_available_slots_rows", JSON.stringify(saved.slotRows));
    } catch (_error) {
      // Ignore storage failures.
    }

    // Close active job on portal so a new booking can be sent / extension is free.
    setJob(null);
    setJobStatus(null);
    navigate("/available-slots", { replace: false });
  }, [
    applicant.applicationCentre,
    applicant.appointmentCategory,
    applicant.subCategory,
    applicationCentre?.centre_name,
    appointmentCategory?.category_name,
    destinationCountry?.iso_code,
    job?.destinationCode,
    job?.jobId,
    job?.originCode,
    navigate,
    originCountry?.iso_code,
    subCategory?.sub_category_name,
  ]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const alertType = params.get("alert");
    const jobIdFromQuery = params.get("jobId");
    const storedToast = (() => {
      try {
        return sessionStorage.getItem("vfs_no_appointment_toast");
      } catch (_error) {
        return null;
      }
    })();
    const storedSuccessToast = (() => {
      try {
        return sessionStorage.getItem("vfs_waitlist_success_toast");
      } catch (_error) {
        return null;
      }
    })();
    const storedBookingFormToast = (() => {
      try {
        return sessionStorage.getItem("vfs_booking_form_success_toast");
      } catch (_error) {
        return null;
      }
    })();
    const storedAvailableSlotsToast = (() => {
      try {
        return sessionStorage.getItem("vfs_available_slots_toast");
      } catch (_error) {
        return null;
      }
    })();

    if (alertType === "no_appointment" || storedToast) {
      showNoAppointmentToast(
        storedToast || "No appointment slots are currently available. Please try again later."
      );
      try {
        sessionStorage.removeItem("vfs_no_appointment_toast");
      } catch (_error) {
        // Ignore storage failures.
      }
    }

    if (alertType === "waitlist_success" || storedSuccessToast) {
      showWaitlistSuccessToast(
        storedSuccessToast || "Waitlist request submitted successfully."
      );
      try {
        sessionStorage.removeItem("vfs_waitlist_success_toast");
      } catch (_error) {
        // Ignore storage failures.
      }
    }

    if (alertType === "booking_form_success" || storedBookingFormToast) {
      showBookingFormSuccessToast(
        storedBookingFormToast || "Online visa form submitted successfully."
      );
      try {
        sessionStorage.removeItem("vfs_booking_form_success_toast");
      } catch (_error) {
        // Ignore storage failures.
      }
    }

    if (alertType === "available_slots" || storedAvailableSlotsToast) {
      let storedRows = null;
      try {
        storedRows = JSON.parse(sessionStorage.getItem("vfs_available_slots_rows") || "null");
      } catch (_error) {
        storedRows = null;
      }
      openAvailableSlotsPage({
        message: storedAvailableSlotsToast || "",
        slotRows: Array.isArray(storedRows) ? storedRows : [],
        jobId: jobIdFromQuery || null,
      });
      try {
        sessionStorage.removeItem("vfs_available_slots_toast");
        sessionStorage.removeItem("vfs_available_slots_rows");
      } catch (_error) {
        // Ignore storage failures.
      }
    }

    if (jobIdFromQuery) {
      API.get(`/vfs/jobs/${jobIdFromQuery}`)
        .then((res) => {
          const jobRow = res.data;
          if (!jobRow) return;
          setJob({
            jobId: jobRow.id,
            originCode: jobRow.origin_code,
            destinationCode: jobRow.dest_code,
            loginUrl: jobRow.login_url,
            applicantData: jobRow.applicantData || jobRow.payload_json || null,
            status: jobRow.status,
            extensionClients: 1,
          });
          setJobStatus(jobRow);
          if (jobRow.applicantData && typeof jobRow.applicantData === "object") {
            setApplicant((prev) => ({ ...prev, ...jobRow.applicantData }));
          }
        })
        .catch(() => {});
    }

    if (alertType || jobIdFromQuery) {
      window.history.replaceState({}, "", "/booking-details");
    }

    const onExtensionAlert = (event) => {
      showNoAppointmentToast(
        event?.detail?.message || "No appointment slots are currently available. Please try again later."
      );
    };
    const onWaitlistSuccess = (event) => {
      showWaitlistSuccessToast(
        event?.detail?.message || "Waitlist request submitted successfully."
      );
    };
    const onBookingFormSuccess = (event) => {
      showBookingFormSuccessToast(
        event?.detail?.message || "Online visa form submitted successfully."
      );
    };
    const onAvailableSlots = (event) => {
      openAvailableSlotsPage({
        message: event?.detail?.message || "",
        slotRows: event?.detail?.slotRows || [],
        months: event?.detail?.months || [],
        slots: event?.detail?.slots || [],
        jobId: event?.detail?.jobId || null,
        originCode: event?.detail?.originCode || "",
        destCode: event?.detail?.destCode || "",
        applicationCentre: event?.detail?.applicationCentre || "",
        appointmentCategory: event?.detail?.appointmentCategory || "",
        subCategory: event?.detail?.subCategory || "",
      });
    };
    window.addEventListener("vfs-no-appointment", onExtensionAlert);
    window.addEventListener("vfs-waitlist-success", onWaitlistSuccess);
    window.addEventListener("vfs-booking-form-success", onBookingFormSuccess);
    window.addEventListener("vfs-available-slots", onAvailableSlots);
    return () => {
      window.removeEventListener("vfs-no-appointment", onExtensionAlert);
      window.removeEventListener("vfs-waitlist-success", onWaitlistSuccess);
      window.removeEventListener("vfs-booking-form-success", onBookingFormSuccess);
      window.removeEventListener("vfs-available-slots", onAvailableSlots);
    };
  }, [openAvailableSlotsPage]);

  useEffect(() => {
    if (!job?.jobId) {
      return undefined;
    }

    let active = true;
    const ws = new WebSocket(getFrontendBridgeUrl());

    const fetchCurrentJob = async () => {
      try {
        const res = await API.get(`/vfs/jobs/${job.jobId}`);
        if (active) {
          setJobStatus(res.data);
          const resultMsg = res.data?.result_msg || "";
          if (
            res.data?.status === "failed" &&
            /no appointment/i.test(resultMsg) &&
            !noAppointmentToastShownRef.current
          ) {
            const originLabel = originCountry?.name || job.originCode || "Origin";
            const destinationLabel = destinationCountry?.name || job.destinationCode || "Destination";
            showNoAppointmentToast(
              `No appointment available from ${originLabel} to ${destinationLabel}.`
            );
          }
        }
      } catch (_error) {
        // Ignore transient load failures and wait for live updates.
      }
    };

    fetchCurrentJob();

    ws.onmessage = (event) => {
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch {
        return;
      }

      if (payload.type !== "job_update" || Number(payload.jobId) !== Number(job.jobId)) {
        return;
      }

      setJobStatus((previous) => ({
        ...(previous || {}),
        status: payload.status,
        result_msg: payload.message ?? previous?.result_msg ?? null,
      }));

      const isNoAppointment =
        payload.eventType === "no_appointment" ||
        /no appointment/i.test(String(payload.message || ""));

      if (isNoAppointment) {
        const originLabel = originCountry?.name || job.originCode || payload.meta?.originCode || "Origin";
        const destinationLabel = destinationCountry?.name || job.destinationCode || payload.meta?.destCode || "Destination";
        const categoryLabel = payload.meta?.subCategory || payload.meta?.appointmentCategory || applicant.subCategory || applicant.appointmentCategory;
        const detailSuffix = categoryLabel ? ` (${categoryLabel})` : "";
        showNoAppointmentToast(
          `No appointment available from ${originLabel} to ${destinationLabel}${detailSuffix}.`
        );
      } else if (payload.eventType === "waitlist_success") {
        showWaitlistSuccessToast(
          payload.message || "Waitlist request submitted successfully."
        );
      } else if (payload.eventType === "booking_form_success") {
        showBookingFormSuccessToast(
          payload.message || "Online visa form submitted successfully."
        );
      } else if (payload.eventType === "available_slots") {
        const slotRows = Array.isArray(payload.meta?.slotRows) ? payload.meta.slotRows : [];
        const slots = Array.isArray(payload.meta?.slots) ? payload.meta.slots : [];
        openAvailableSlotsPage({
          jobId: payload.jobId,
          message: payload.message || "",
          slotRows,
          slots,
          months: Array.isArray(payload.meta?.months) ? payload.meta.months : [],
          originCode: payload.meta?.originCode || "",
          destCode: payload.meta?.destCode || "",
          applicationCentre: payload.meta?.applicationCentre || "",
          appointmentCategory: payload.meta?.appointmentCategory || "",
          subCategory: payload.meta?.subCategory || "",
        });
      }
    };

    return () => {
      active = false;
      ws.close();
    };
  }, [
    applicant.appointmentCategory,
    applicant.subCategory,
    destinationCountry?.name,
    job,
    openAvailableSlotsPage,
    originCountry?.name,
  ]);

  useEffect(() => {
    if (!toast) {
      return undefined;
    }

    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
    }, 20000);

    return () => clearTimeout(toastTimerRef.current);
  }, [toast]);

  const handleOriginCountryChange = (country) => {
    setOriginCountry(country);
    setApplicationCentre(null);
    setAppointmentCategory(null);
    setSubCategory(null);
    setApplicant((prev) => ({
      ...prev,
      applicationCentre: "",
      appointmentCategory: "",
      subCategory: "",
    }));
  };

  const handleDestinationCountryChange = (country) => {
    setDestinationCountry(country);
    setApplicationCentre(null);
    setAppointmentCategory(null);
    setSubCategory(null);
    setApplicant((prev) => ({
      ...prev,
      applicationCentre: "",
      appointmentCategory: "",
      subCategory: "",
    }));
    // Multi-step (passport → additional) is Switzerland-only.
    if (!isSwitzerlandDestination(country)) {
      setCurrentStep(1);
      setError("");
    }
  };

  const isSwitzerlandFlow = isSwitzerlandDestination(destinationCountry);

  const handleApplicationCentreChange = (centre) => {
    setApplicationCentre(centre);
    setAppointmentCategory(null);
    setSubCategory(null);
    setApplicant((prev) => ({
      ...prev,
      applicationCentre: centre?.centre_name || "",
      appointmentCategory: "",
      subCategory: "",
    }));
  };

  const handleAppointmentCategoryChange = (category) => {
    setAppointmentCategory(category);
    setSubCategory(null);
    setApplicant((prev) => ({
      ...prev,
      appointmentCategory: category?.category_name || "",
      subCategory: "",
    }));
  };

  const handleSubCategoryChange = (selectedSubCategory) => {
    setSubCategory(selectedSubCategory);
    setApplicant((prev) => ({
      ...prev,
      subCategory: selectedSubCategory?.sub_category_name || "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    // Switzerland multi-step: form submit on steps 2–5 advances, not send.
    if (
      isSwitzerlandFlow &&
      (currentStep === 2 || currentStep === 3 || currentStep === 4 || currentStep === 5)
    ) {
      handleNextStep();
      return;
    }

    if (!originCountry) {
      setError("Please select an Origin / Departure country.");
      return;
    }

    if (!destinationCountry) {
      setError("Please select a Destination / Arrival country.");
      return;
    }

    if (!applicationCentre || !appointmentCategory || !subCategory) {
      setError("Please complete Application Centre, Appointment Category and Sub Category.");
      return;
    }

    const isFullFormSubmit = isSwitzerlandFlow && currentStep === 6;
    const applicantData = isFullFormSubmit
      ? {
          ...applicant,
          passportNumberConfirmation: applicant.passportNumber,
        }
      : withQuickSendPassportAndApplicationFields(applicant);

    const requiredFields = isFullFormSubmit
      ? [
          ...STEP_ONE_REQUIRED_FIELDS,
          ...STEP_TWO_REQUIRED_FIELDS,
          ...STEP_THREE_REQUIRED_FIELDS,
          ...STEP_FOUR_REQUIRED_FIELDS,
          ...STEP_FIVE_REQUIRED_FIELDS,
          ...(getStepFiveAccommodationRequiredFields(applicantData.invitingPartyType)),
          ...STEP_SIX_REQUIRED_FIELDS,
          ...(String(applicantData.transitVisa || "").trim() === "Yes"
            ? STEP_SIX_TRANSIT_REQUIRED_FIELDS
            : []),
          ...(String(applicantData.costsCoveredByInvitingParty || "").trim() === "No"
            ? ["costsCoveredBy"]
            : []),
        ]
      : STEP_ONE_REQUIRED_FIELDS;

    const missingField = requiredFields.find((key) => !isApplicantFieldFilled(applicantData, key));

    if (missingField) {
      setError(`Please fill required field: ${FIELD_LABELS[missingField] || missingField}`);
      return;
    }

    if (applicantData.passportIssueDate > applicantData.passportExpiry) {
      setError("Passport Date of Issue must be before Passport Expiry.");
      return;
    }

    if (
      applicantData.schengenArrivalDate &&
      applicantData.schengenDepartureDate &&
      applicantData.schengenArrivalDate > applicantData.schengenDepartureDate
    ) {
      setError("Date Of Arrival in Schengen Area must be before Date of Departure.");
      return;
    }

    if (
      String(applicantData.transitVisa || "").trim() === "Yes" &&
      applicantData.transitValidFrom &&
      applicantData.transitValidUntil &&
      applicantData.transitValidFrom > applicantData.transitValidUntil
    ) {
      setError("Valid from must be before Valid until.");
      return;
    }

    setLoading(true);

    try {
      const res = await API.post("/vfs/jobs", {
        originCode: originCountry.iso_code,
        destinationCode: destinationCountry.iso_code,
        applicantData,
      });
      setJob(res.data);
    } catch (submitError) {
      setError(submitError.response?.data?.message || "Failed to create job. Is the backend running?");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setOriginCountry(null);
    setDestinationCountry(null);
    setApplicationCentre(null);
    setAppointmentCategory(null);
    setSubCategory(null);
    setApplicant(createInitialApplicant());
    setJob(null);
    setJobStatus(null);
    setError("");
    setToast(null);
    setCurrentStep(1);
  };

  const setApplicantField = (field) => (event) => {
    setApplicant((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleNextStep = () => {
    setError("");

    if (!isSwitzerlandFlow) {
      return;
    }

    if (currentStep === 1) {
      if (!originCountry) {
        setError("Please select an Origin / Departure country.");
        return;
      }

      if (!destinationCountry) {
        setError("Please select a Destination / Arrival country.");
        return;
      }

      if (!applicationCentre || !appointmentCategory || !subCategory) {
        setError("Please complete Application Centre, Appointment Category and Sub Category.");
        return;
      }

      const missingField = STEP_ONE_REQUIRED_FIELDS.find(
        (key) => !String(applicant[key] || "").trim()
      );

      if (missingField) {
        setError(`Please fill required field: ${FIELD_LABELS[missingField] || missingField}`);
        return;
      }

      setApplicant((prev) => ({
        ...prev,
        passportNumberConfirmation: prev.passportNumber,
      }));
      setCurrentStep(2);
    } else if (currentStep === 2) {
      const missingField = STEP_TWO_REQUIRED_FIELDS.find(
        (key) => !String(applicant[key] || "").trim()
      );
      if (missingField) {
        setError(`Please fill required field: ${FIELD_LABELS[missingField] || missingField}`);
        return;
      }
      if (applicant.passportIssueDate > applicant.passportExpiry) {
        setError("Passport Date of Issue must be before Passport Expiry.");
        return;
      }

      setApplicant((prev) => ({
        ...prev,
        applicationHomeAddress: prev.applicationHomeAddress || prev.addressLine1,
        applicationStreet: prev.applicationStreet || prev.addressLine2,
        applicantCountry: prev.applicantCountry || prev.nationality,
        applicationEmail: prev.applicationEmail || prev.email,
        applicationPhoneCode: prev.applicationPhoneCode || prev.phoneCode,
        applicationTelephone: prev.applicationTelephone || prev.contactNumber,
        employerAddress: prev.employerAddress || prev.addressLine1,
        employerTelephone: prev.employerTelephone || prev.contactNumber,
        employerCity: prev.employerCity || prev.applicationCity,
        employerPostalCode: prev.employerPostalCode || prev.applicantPostalCode,
        employerCountry: prev.employerCountry || prev.nationality,
      }));
      setCurrentStep(3);
    } else if (currentStep === 3) {
      const missingField = STEP_THREE_REQUIRED_FIELDS.find(
        (key) => !String(applicant[key] || "").trim()
      );
      if (missingField) {
        setError(`Please fill required field: ${FIELD_LABELS[missingField] || missingField}`);
        return;
      }

      setApplicant((prev) => ({
        ...prev,
        purposeOfTravel: prev.purposeOfTravel || "Tourism",
        purposeOfStayDetails: prev.purposeOfStayDetails || "TOURISM",
        numberOfEntries: prev.numberOfEntries || "Single entry",
        schengenVisaPastThreeYears: prev.schengenVisaPastThreeYears || "No",
        travelInsurance: prev.travelInsurance || "Yes",
      }));
      setCurrentStep(4);
    } else if (currentStep === 4) {
      const missingField = STEP_FOUR_REQUIRED_FIELDS.find(
        (key) => !String(applicant[key] || "").trim()
      );
      if (missingField) {
        setError(`Please fill required field: ${FIELD_LABELS[missingField] || missingField}`);
        return;
      }

      setApplicant((prev) => ({
        ...prev,
        invitingPartyType: prev.invitingPartyType || "Hotel",
        hotelAddress: prev.hotelAddress || prev.applicationHomeAddress || prev.addressLine1,
        hotelPostalCode: prev.hotelPostalCode || prev.applicantPostalCode,
        hotelEmail: prev.hotelEmail || prev.applicationEmail || prev.email,
        hotelStreet: prev.hotelStreet || prev.applicationStreet || prev.addressLine1,
        hotelCity: prev.hotelCity || prev.applicationCity,
        hotelCountry: prev.hotelCountry || prev.applicantCountry || prev.nationality,
        hotelPhoneCode: prev.hotelPhoneCode || prev.applicationPhoneCode || prev.phoneCode,
        hotelTelephone: prev.hotelTelephone || prev.applicationTelephone || prev.contactNumber,
      }));
      setCurrentStep(5);
    } else if (currentStep === 5) {
      const accommodationRequired = [
        ...STEP_FIVE_REQUIRED_FIELDS,
        ...getStepFiveAccommodationRequiredFields(applicant.invitingPartyType),
      ];
      const missingField = accommodationRequired.find((key) => !isApplicantFieldFilled(applicant, key));
      if (missingField) {
        setError(`Please fill required field: ${FIELD_LABELS[missingField] || missingField}`);
        return;
      }

      setApplicant((prev) => ({
        ...prev,
        transitVisa: prev.transitVisa || "No",
        host: prev.host || prev.lastName || "HOST",
        formFillerName:
          prev.formFillerName || `${prev.firstName || ""} ${prev.lastName || ""}`.trim(),
        hostCompanyOrHotel: prev.hostCompanyOrHotel || prev.hotelName || "HOTEL",
        hostFullAddress: prev.hostFullAddress || prev.applicationHomeAddress || prev.addressLine1,
        hostTelephone: prev.hostTelephone || prev.applicationTelephone || prev.contactNumber,
        hostEmail: prev.hostEmail || prev.applicationEmail || prev.email,
        schengenArrivalDate: prev.schengenArrivalDate || "",
        schengenDepartureDate: prev.schengenDepartureDate || "",
        durationOfStay: prev.durationOfStay || "",
        costCoveredBy:
          Array.isArray(prev.costCoveredBy) && prev.costCoveredBy.length
            ? prev.costCoveredBy
            : ["By the applicant himself / herself"],
        meansOfSupport:
          Array.isArray(prev.meansOfSupport) && prev.meansOfSupport.length
            ? prev.meansOfSupport
            : ["Cash"],
        schengenStateFirstEntry: prev.schengenStateFirstEntry || "Austria",
        destinationCountries:
          Array.isArray(prev.destinationCountries) && prev.destinationCountries.length
            ? prev.destinationCountries
            : ["Austria"],
        costsCoveredByInvitingParty: prev.costsCoveredByInvitingParty || "Yes",
      }));
      setCurrentStep(6);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePreviousStep = () => {
    setError("");
    setCurrentStep((step) => Math.max(1, step - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const STATUS_COLORS = {
    queued: { bg: "#fff6e0", color: "#7a5a08", label: "Queued" },
    dispatched: { bg: "#e8f1f6", color: "#3d6f8c", label: "Dispatched to Extension" },
    processing: { bg: "#e6f3f1", color: "#0f6b63", label: "Processing…" },
    success: { bg: "#e8f5ec", color: "#2f7d4a", label: "Success ✓" },
    failed: { bg: "#fdecec", color: "#b23a3a", label: "Failed ✗" },
  };

  const liveStatus = jobStatus?.status ?? job?.status ?? "queued";
  const sc = STATUS_COLORS[liveStatus] ?? STATUS_COLORS.queued;
  const connectionLabel = (job?.extensionClients ?? 0) > 0
    ? `Connected (${job.extensionClients} client${job.extensionClients > 1 ? "s" : ""})`
    : "Waiting for extension";

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="flex-grow-1 booking-page app-page">
        <div className="booking-page-shell">
          {toast && (
            <div
              className={`booking-toast booking-toast-${toast.variant}`}
              role="alert"
              aria-live="assertive"
            >
              <div className="booking-toast-content">
                <strong className="booking-toast-title">{toast.title || "Notice"}</strong>
                <span style={{ whiteSpace: "pre-wrap" }}>{toast.message}</span>
              </div>
              <button type="button" className="booking-toast-close" onClick={() => setToast(null)}>
                ×
              </button>
            </div>
          )}

          <div className="page-header">
            <div>
              <h1 className="page-header-title">VFS Booking</h1>
              <p className="page-header-desc">
                Fill step 1 and send now, or continue through passport, application, travel, and
                accommodation details for full VFS form filling.
              </p>
            </div>
          </div>

          <div className="row g-4 booking-content-grid">
            <div className={job ? "col-xl-8" : "col-12"}>
              <section className="booking-panel">
                <div className="booking-panel-body">
                  {!job && isSwitzerlandFlow && (
                    <div className="booking-stepper" aria-label="Booking form progress">
                      <div className="booking-stepper-track">
                        {FORM_STEPS.map((step, index) => {
                          const stateClass =
                            currentStep === step.id
                              ? "is-active"
                              : currentStep > step.id
                                ? "is-complete"
                                : "";
                          return (
                            <div key={step.id} className={`booking-step ${stateClass}`.trim()}>
                              {index > 0 ? <span className="booking-step-connector" aria-hidden="true" /> : null}
                              <span className="booking-step-node">
                                <span className="booking-step-number">
                                  {currentStep > step.id ? "✓" : step.id}
                                </span>
                              </span>
                              <span className="booking-step-copy">
                                <strong>{step.title}</strong>
                                <small>{step.subtitle}</small>
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="booking-stepper-meta">
                        <span className="booking-stepper-pill">
                          Step {currentStep} · {FORM_STEPS[currentStep - 1]?.title}
                        </span>
                        <span className="booking-stepper-hint">
                          {FORM_STEPS[currentStep - 1]?.subtitle}
                        </span>
                      </div>
                    </div>
                  )}

                  {error && <div className="alert alert-danger py-2 mt-0 mb-3">{error}</div>}

                  <form onSubmit={handleSubmit}>
                    <div className="booking-form-body">
                    {(currentStep === 1 || job) && (
                    <>
                    <div className="booking-form-section">
                      <div className="booking-section-head">
                        <span className="booking-section-icon">
                          <FiGlobe size={18} />
                        </span>
                        <div>
                          <h3 className="booking-section-title">Travel Information</h3>
                        </div>
                      </div>

                      <VisaRouteSelectors
                        disabled={!!job}
                        selectedOriginCountryId={originCountry?.id || ""}
                        selectedDestinationCountryId={destinationCountry?.id || ""}
                        selectedApplicationCentreId={applicationCentre?.id || ""}
                        selectedAppointmentCategoryId={appointmentCategory?.id || ""}
                        selectedSubCategoryId={subCategory?.id || ""}
                        onOriginCountryChange={handleOriginCountryChange}
                        onDestinationCountryChange={handleDestinationCountryChange}
                        onApplicationCentreChange={handleApplicationCentreChange}
                        onAppointmentCategoryChange={handleAppointmentCategoryChange}
                        onSubCategoryChange={handleSubCategoryChange}
                      />
                    </div>

                    <div className="booking-form-section">
                      <div className="booking-section-head">
                        <span className="booking-section-icon">
                          <FiUser size={18} />
                        </span>
                        <div>
                          <h3 className="booking-section-title">Personal Details</h3>
                        </div>
                      </div>

                      <div className="row g-3 booking-fields">
                        <div className="col-md-6">
                          <label className="form-label">First Name *</label>
                          <input className="form-control booking-input" value={applicant.firstName} onChange={setApplicantField("firstName")} disabled={!!job} placeholder="Enter first name" />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Last Name *</label>
                          <input className="form-control booking-input" value={applicant.lastName} onChange={setApplicantField("lastName")} disabled={!!job} placeholder="Enter last name" />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Gender *</label>
                          <select
                            className="form-select booking-input"
                            value={applicant.gender}
                            onChange={setApplicantField("gender")}
                            disabled={!!job}
                          >
                            <option value="">Select gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Date of Birth *</label>
                          <input type="date" className="form-control booking-input" value={applicant.dateOfBirth} onChange={setApplicantField("dateOfBirth")} disabled={!!job} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Nationality *</label>
                          <Select
                            classNamePrefix="nationality-select"
                            options={nationalityOptions}
                            value={selectedNationalityOption}
                            onChange={(option) =>
                              setApplicant((prev) => ({
                                ...prev,
                                nationality: String(option?.value || "").toUpperCase(),
                              }))
                            }
                            isDisabled={!!job || nationalitiesLoading}
                            isLoading={nationalitiesLoading}
                            isClearable
                            isSearchable
                            placeholder="Search nationality"
                            noOptionsMessage={() => "No nationality found"}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Passport Number *</label>
                          <input className="form-control booking-input" value={applicant.passportNumber} onChange={setApplicantField("passportNumber")} disabled={!!job} placeholder="Passport number" />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Passport Expiry *</label>
                          <input type="date" className="form-control booking-input" value={applicant.passportExpiry} onChange={setApplicantField("passportExpiry")} disabled={!!job} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Phone Code *</label>
                          <input className="form-control booking-input" value={applicant.phoneCode} onChange={setApplicantField("phoneCode")} disabled={!!job} placeholder="44" />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Contact Number *</label>
                          <input className="form-control booking-input" value={applicant.contactNumber} onChange={setApplicantField("contactNumber")} disabled={!!job} placeholder="Contact number" />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Email *</label>
                          <input type="email" className="form-control booking-input" value={applicant.email} onChange={setApplicantField("email")} disabled={!!job} placeholder="Enter email address" />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Address Line 1 *</label>
                          <input className="form-control booking-input" value={applicant.addressLine1} onChange={setApplicantField("addressLine1")} disabled={!!job} placeholder="Enter address line 1" />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Address Line 2</label>
                          <input className="form-control booking-input" value={applicant.addressLine2} onChange={setApplicantField("addressLine2")} disabled={!!job} placeholder="Enter address line 2" />
                        </div>
                      </div>
                    </div>
                    </>
                    )}

                    {isSwitzerlandFlow && currentStep === 2 && !job && (
                      <div className="booking-form-section">
                        <div className="booking-section-head">
                          <span className="booking-section-icon">
                            <FiFileText size={18} />
                          </span>
                          <div>
                            <h3 className="booking-section-title">Passport Details</h3>
                            <p className="booking-panel-subtitle">
                              Enter values exactly as shown on the applicant’s passport.
                            </p>
                          </div>
                        </div>

                        <div className="row g-3 booking-fields">
                          <div className="col-md-6">
                            <label className="form-label">Surname at Birth *</label>
                            <input
                              className="form-control booking-input text-uppercase"
                              value={applicant.surnameAtBirth}
                              onChange={(event) =>
                                setApplicant((prev) => ({
                                  ...prev,
                                  surnameAtBirth: event.target.value.toUpperCase(),
                                }))
                              }
                              placeholder="Enter surname at birth"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Country of Birth *</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={nationalityOptions}
                              value={getApplicantOption("countryOfBirth")}
                              onChange={setApplicantSelectField("countryOfBirth", true)}
                              isDisabled={nationalitiesLoading}
                              isLoading={nationalitiesLoading}
                              isClearable
                              isSearchable
                              placeholder="Search country of birth"
                              noOptionsMessage={() => "No country found"}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Place of Birth *</label>
                            <input
                              className="form-control booking-input text-uppercase"
                              value={applicant.placeOfBirth}
                              onChange={(event) =>
                                setApplicant((prev) => ({
                                  ...prev,
                                  placeOfBirth: event.target.value.toUpperCase(),
                                }))
                              }
                              placeholder="Enter place of birth"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Nationality at Birth *</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={nationalityOptions}
                              value={getApplicantOption("nationalityAtBirth")}
                              onChange={setApplicantSelectField("nationalityAtBirth", true)}
                              isDisabled={nationalitiesLoading}
                              isLoading={nationalitiesLoading}
                              isClearable
                              isSearchable
                              placeholder="Search nationality at birth"
                              noOptionsMessage={() => "No nationality found"}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Other Nationality</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={nationalityOptions}
                              value={getApplicantOption("otherNationality")}
                              onChange={setApplicantSelectField("otherNationality", true)}
                              isDisabled={nationalitiesLoading}
                              isLoading={nationalitiesLoading}
                              isClearable
                              isSearchable
                              placeholder="Select only if applicable"
                              noOptionsMessage={() => "No nationality found"}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Marital Status *</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={MARITAL_STATUS_OPTIONS}
                              value={getApplicantOption("maritalStatus", MARITAL_STATUS_OPTIONS)}
                              onChange={setApplicantSelectField("maritalStatus")}
                              isClearable
                              isSearchable
                              placeholder="Select marital status"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Applicant ID Number</label>
                            <input
                              className="form-control booking-input text-uppercase"
                              value={applicant.applicantIdNumber}
                              onChange={(event) =>
                                setApplicant((prev) => ({
                                  ...prev,
                                  applicantIdNumber: event.target.value.toUpperCase(),
                                }))
                              }
                              placeholder="Optional"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Type of Passport *</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={PASSPORT_TYPE_OPTIONS}
                              value={getApplicantOption("passportType", PASSPORT_TYPE_OPTIONS)}
                              onChange={setApplicantSelectField("passportType")}
                              isClearable
                              isSearchable
                              placeholder="Select passport type"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Re-enter Passport Number</label>
                            <input
                              className="form-control booking-input text-uppercase"
                              value={applicant.passportNumber}
                              disabled
                            />
                            <div className="booking-field-note">
                              Automatically copied from Step 1.
                            </div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Passport Date of Issue *</label>
                            <input
                              type="date"
                              className="form-control booking-input"
                              value={applicant.passportIssueDate}
                              onChange={setApplicantField("passportIssueDate")}
                              max={applicant.passportExpiry || undefined}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Issued Country *</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={nationalityOptions}
                              value={getApplicantOption("passportIssuedCountry")}
                              onChange={setApplicantSelectField("passportIssuedCountry", true)}
                              isDisabled={nationalitiesLoading}
                              isLoading={nationalitiesLoading}
                              isClearable
                              isSearchable
                              placeholder="Search issued country"
                              noOptionsMessage={() => "No country found"}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Issued By *</label>
                            <input
                              className="form-control booking-input text-uppercase"
                              value={applicant.passportIssuedBy}
                              onChange={(event) =>
                                setApplicant((prev) => ({
                                  ...prev,
                                  passportIssuedBy: event.target.value.toUpperCase(),
                                }))
                              }
                              placeholder="Enter issuing authority"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {isSwitzerlandFlow && currentStep === 3 && !job && (
                      <div className="booking-form-section">
                        <div className="booking-section-head">
                          <span className="booking-section-icon">
                            <FiFileText size={18} />
                          </span>
                          <div>
                            <h3 className="booking-section-title">Application Details</h3>
                            <p className="booking-panel-subtitle">
                              Confirm the address and contact details used by VFS.
                            </p>
                          </div>
                        </div>

                        <div className="row g-3 booking-fields">
                          <div className="col-md-6">
                            <label className="form-label">Applicant Home Address *</label>
                            <input className="form-control booking-input text-uppercase" value={applicant.applicationHomeAddress} onChange={setApplicantField("applicationHomeAddress")} placeholder="Enter home address" />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Application Street *</label>
                            <input className="form-control booking-input text-uppercase" value={applicant.applicationStreet} onChange={setApplicantField("applicationStreet")} placeholder="Enter street" />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Application City *</label>
                            <input className="form-control booking-input text-uppercase" value={applicant.applicationCity} onChange={setApplicantField("applicationCity")} placeholder="Enter city" />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Applicant Country *</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={nationalityOptions}
                              value={getApplicantOption("applicantCountry")}
                              onChange={setApplicantSelectField("applicantCountry", true)}
                              isDisabled={nationalitiesLoading}
                              isLoading={nationalitiesLoading}
                              isClearable
                              isSearchable
                              placeholder="Search applicant country"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Applicant’s Postal Code *</label>
                            <input className="form-control booking-input text-uppercase" value={applicant.applicantPostalCode} onChange={setApplicantField("applicantPostalCode")} placeholder="Enter postal code" />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">EU / EEA / CH / UK Family Member *</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={NO_ONLY_OPTIONS}
                              value={getApplicantOption("hasEuFamilyMember", NO_ONLY_OPTIONS)}
                              isDisabled
                            />
                            <div className="booking-field-note">Only No is supported until the conditional VFS fields are added.</div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Current Occupation *</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={OCCUPATION_OPTIONS}
                              value={getApplicantOption("currentOccupation", OCCUPATION_OPTIONS)}
                              onChange={setApplicantSelectField("currentOccupation")}
                              isClearable
                              isSearchable
                              placeholder="Search current occupation"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Applicant Email Address *</label>
                            <input type="email" className="form-control booking-input" value={applicant.applicationEmail} onChange={setApplicantField("applicationEmail")} placeholder="Enter email address" />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Telephone Code *</label>
                            <input className="form-control booking-input" value={applicant.applicationPhoneCode} onChange={setApplicantField("applicationPhoneCode")} placeholder="44" />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Applicant Telephone *</label>
                            <input className="form-control booking-input" value={applicant.applicationTelephone} onChange={setApplicantField("applicationTelephone")} placeholder="Enter telephone number" />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Residence Outside Current Nationality *</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={NO_ONLY_OPTIONS}
                              value={getApplicantOption("residenceOtherCountry", NO_ONLY_OPTIONS)}
                              isDisabled
                            />
                            <div className="booking-field-note">Only No is supported until the conditional VFS fields are added.</div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Name of Employer/Education establishment *</label>
                            <input
                              className="form-control booking-input text-uppercase"
                              value={applicant.employerName}
                              maxLength={50}
                              onChange={setApplicantField("employerName")}
                              placeholder="Enter employer or education establishment"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Employer/Education establishment Address *</label>
                            <input
                              className="form-control booking-input text-uppercase"
                              value={applicant.employerAddress}
                              maxLength={101}
                              onChange={setApplicantField("employerAddress")}
                              placeholder="Enter employer address"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Employer/Education establishment Telephone *</label>
                            <input
                              className="form-control booking-input"
                              value={applicant.employerTelephone}
                              maxLength={50}
                              onChange={setApplicantField("employerTelephone")}
                              placeholder="Enter employer telephone"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Employer/Education establishment City *</label>
                            <input
                              className="form-control booking-input text-uppercase"
                              value={applicant.employerCity}
                              maxLength={100}
                              onChange={setApplicantField("employerCity")}
                              placeholder="Enter employer city"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Employer/Education establishment Postal Code *</label>
                            <input
                              className="form-control booking-input text-uppercase"
                              value={applicant.employerPostalCode}
                              maxLength={100}
                              onChange={setApplicantField("employerPostalCode")}
                              placeholder="Enter employer postal code"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Employer/Education establishment Country *</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={nationalityOptions}
                              value={getApplicantOption("employerCountry")}
                              onChange={setApplicantSelectField("employerCountry", true)}
                              isDisabled={nationalitiesLoading}
                              isLoading={nationalitiesLoading}
                              isClearable
                              isSearchable
                              placeholder="Search employer country"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Fingerprints Collected Previously *</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={NO_ONLY_OPTIONS}
                              value={getApplicantOption("fingerprintsCollectedPreviously", NO_ONLY_OPTIONS)}
                              isDisabled
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {isSwitzerlandFlow && currentStep === 4 && !job && (
                      <div className="booking-form-section">
                        <div className="booking-section-head">
                          <span className="booking-section-icon">
                            <FiGlobe size={18} />
                          </span>
                          <div>
                            <h3 className="booking-section-title">Travel Details</h3>
                            <p className="booking-panel-subtitle">
                              Enter values exactly as they should appear on the VFS travel details page.
                            </p>
                          </div>
                        </div>

                        <div className="row g-3 booking-fields">
                          <div className="col-md-6">
                            <label className="form-label">Purpose Of Travel *</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={PURPOSE_OF_TRAVEL_OPTIONS}
                              value={getApplicantOption("purposeOfTravel", PURPOSE_OF_TRAVEL_OPTIONS)}
                              onChange={setApplicantSelectField("purposeOfTravel")}
                              isClearable
                              isSearchable
                              placeholder="Select purpose of travel"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Additional information on purpose of stay *</label>
                            <input
                              className="form-control booking-input text-uppercase"
                              value={applicant.purposeOfStayDetails}
                              maxLength={100}
                              onChange={(event) =>
                                setApplicant((prev) => ({
                                  ...prev,
                                  purposeOfStayDetails: event.target.value.toUpperCase(),
                                }))
                              }
                              placeholder="Enter additional information"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Number Of Entries Requested *</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={NUMBER_OF_ENTRIES_OPTIONS}
                              value={getApplicantOption("numberOfEntries", NUMBER_OF_ENTRIES_OPTIONS)}
                              onChange={setApplicantSelectField("numberOfEntries")}
                              isClearable
                              isSearchable
                              placeholder="Select number of entries"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Schengen visas in the past three years *</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={NO_ONLY_OPTIONS}
                              value={getApplicantOption("schengenVisaPastThreeYears", NO_ONLY_OPTIONS)}
                              isDisabled
                            />
                            <div className="booking-field-note">Only No is supported until the extra visa-history fields are added.</div>
                          </div>
                          <div className="col-md-12">
                            <label className="form-label">Travel Insurance *</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={YES_NO_OPTIONS}
                              value={getApplicantOption("travelInsurance", YES_NO_OPTIONS)}
                              onChange={setApplicantSelectField("travelInsurance")}
                              isClearable
                              placeholder="Select travel insurance"
                            />
                            <div className="booking-field-note">
                              VFS asks if you are aware of the need for adequate travel medical insurance for visits to the Schengen area.
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {isSwitzerlandFlow && currentStep === 5 && !job && (
                      <div className="booking-form-section">
                        <div className="booking-section-head">
                          <span className="booking-section-icon">
                            <FiGlobe size={18} />
                          </span>
                          <div>
                            <h3 className="booking-section-title">Accommodation Details</h3>
                            <p className="booking-panel-subtitle">
                              Select Hotel, Company, or Other to fill accommodation details. Hotel also asks for hotel name and address.
                            </p>
                          </div>
                        </div>

                        <div className="row g-3 booking-fields">
                          <div className="col-md-6">
                            <label className="form-label">Type of inviting party *</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={INVITING_PARTY_TYPE_OPTIONS}
                              value={getApplicantOption("invitingPartyType", INVITING_PARTY_TYPE_OPTIONS)}
                              onChange={setApplicantSelectField("invitingPartyType")}
                              isClearable
                              isSearchable
                              placeholder="Select type of inviting party"
                            />
                            {applicant.invitingPartyType === "Private Person" && (
                              <div className="booking-field-note">
                                Extra fields for Private Person are not integrated yet. Use Hotel, Company, or Other for a complete fill.
                              </div>
                            )}
                          </div>
                          {showsAccommodationExtraFields(applicant.invitingPartyType) && (
                            <>
                              {applicant.invitingPartyType === "Hotel" && (
                                <>
                                  <div className="col-md-6">
                                    <label className="form-label">Name of the inviting hotel *</label>
                                    <input
                                      className="form-control booking-input text-uppercase"
                                      value={applicant.hotelName}
                                      maxLength={100}
                                      onChange={setApplicantField("hotelName")}
                                      placeholder="Enter hotel name"
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <label className="form-label">Address of the inviting Hotel *</label>
                                    <input
                                      className="form-control booking-input text-uppercase"
                                      value={applicant.hotelAddress}
                                      maxLength={100}
                                      onChange={setApplicantField("hotelAddress")}
                                      placeholder="Enter hotel address"
                                    />
                                  </div>
                                </>
                              )}
                              <div className="col-md-6">
                                <label className="form-label">Postal Code *</label>
                                <input
                                  className="form-control booking-input text-uppercase"
                                  value={applicant.hotelPostalCode}
                                  maxLength={100}
                                  onChange={setApplicantField("hotelPostalCode")}
                                  placeholder="Enter hotel postal code"
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Post Office Box</label>
                                <input
                                  className="form-control booking-input text-uppercase"
                                  value={applicant.hotelPostOfficeBox}
                                  maxLength={100}
                                  onChange={setApplicantField("hotelPostOfficeBox")}
                                  placeholder="Optional"
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Post Office Box Number</label>
                                <input
                                  className="form-control booking-input text-uppercase"
                                  value={applicant.hotelPostOfficeBoxNumber}
                                  maxLength={100}
                                  onChange={setApplicantField("hotelPostOfficeBoxNumber")}
                                  placeholder="Optional"
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Email *</label>
                                <input
                                  type="email"
                                  className="form-control booking-input"
                                  value={applicant.hotelEmail}
                                  maxLength={100}
                                  onChange={setApplicantField("hotelEmail")}
                                  placeholder="Enter hotel email"
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Street *</label>
                                <input
                                  className="form-control booking-input text-uppercase"
                                  value={applicant.hotelStreet}
                                  maxLength={100}
                                  onChange={setApplicantField("hotelStreet")}
                                  placeholder="Enter hotel street"
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">City *</label>
                                <input
                                  className="form-control booking-input text-uppercase"
                                  value={applicant.hotelCity}
                                  maxLength={100}
                                  onChange={setApplicantField("hotelCity")}
                                  placeholder="Enter hotel city"
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Country *</label>
                                <Select
                                  classNamePrefix="nationality-select"
                                  options={nationalityOptions}
                                  value={getApplicantOption("hotelCountry")}
                                  onChange={setApplicantSelectField("hotelCountry", true)}
                                  isDisabled={nationalitiesLoading}
                                  isLoading={nationalitiesLoading}
                                  isClearable
                                  isSearchable
                                  placeholder="Search hotel country"
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Telephone Code *</label>
                                <input
                                  className="form-control booking-input"
                                  value={applicant.hotelPhoneCode}
                                  onChange={setApplicantField("hotelPhoneCode")}
                                  placeholder="44"
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Telephone Number *</label>
                                <input
                                  className="form-control booking-input"
                                  value={applicant.hotelTelephone}
                                  maxLength={12}
                                  onChange={setApplicantField("hotelTelephone")}
                                  placeholder="Enter hotel telephone"
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Fax Number</label>
                                <input
                                  className="form-control booking-input"
                                  value={applicant.hotelFax}
                                  onChange={setApplicantField("hotelFax")}
                                  placeholder="Optional"
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Surname of the contact person</label>
                                <input
                                  className="form-control booking-input text-uppercase"
                                  value={applicant.hotelContactSurname}
                                  maxLength={100}
                                  onChange={setApplicantField("hotelContactSurname")}
                                  placeholder="Optional"
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Firstname(-s) of the contact person</label>
                                <input
                                  className="form-control booking-input text-uppercase"
                                  value={applicant.hotelContactFirstName}
                                  maxLength={100}
                                  onChange={setApplicantField("hotelContactFirstName")}
                                  placeholder="Optional"
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Email address of the contact person</label>
                                <input
                                  type="email"
                                  className="form-control booking-input"
                                  value={applicant.hotelContactEmail}
                                  maxLength={50}
                                  onChange={setApplicantField("hotelContactEmail")}
                                  placeholder="Optional"
                                />
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {isSwitzerlandFlow && currentStep === 6 && !job && (
                      <div className="booking-form-section">
                        <div className="booking-section-head">
                          <span className="booking-section-icon">
                            <FiGlobe size={18} />
                          </span>
                          <div>
                            <h3 className="booking-section-title">Additional Details</h3>
                            <p className="booking-panel-subtitle">
                              Enter values exactly as they should appear on the VFS Additional Details page.
                            </p>
                          </div>
                        </div>

                        <div className="row g-3 booking-fields">
                          <div className="col-md-6">
                            <label className="form-label">Transit Visa? *</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={YES_NO_OPTIONS}
                              value={getApplicantOption("transitVisa", YES_NO_OPTIONS)}
                              onChange={setApplicantSelectField("transitVisa")}
                              isClearable
                              placeholder="Select Yes or No"
                            />
                          </div>

                          {String(applicant.transitVisa || "").trim() === "Yes" && (
                            <>
                              <div className="col-md-6">
                                <label className="form-label">Entry permit issued by *</label>
                                <Select
                                  classNamePrefix="nationality-select"
                                  options={ENTRY_PERMIT_ISSUED_BY_OPTIONS}
                                  value={getApplicantOption("entryPermitIssuedBy", ENTRY_PERMIT_ISSUED_BY_OPTIONS)}
                                  onChange={setApplicantSelectField("entryPermitIssuedBy")}
                                  isClearable
                                  isSearchable
                                  placeholder="Select issuing country"
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Valid from *</label>
                                <input
                                  type="date"
                                  className="form-control booking-input"
                                  value={applicant.transitValidFrom}
                                  onChange={setApplicantField("transitValidFrom")}
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Valid until *</label>
                                <input
                                  type="date"
                                  className="form-control booking-input"
                                  value={applicant.transitValidUntil}
                                  onChange={setApplicantField("transitValidUntil")}
                                />
                                <div className="booking-field-note">
                                  Must be before the intended travel date on VFS.
                                </div>
                              </div>
                            </>
                          )}

                          <div className="col-md-6">
                            <label className="form-label">Host</label>
                            <input
                              className="form-control booking-input text-uppercase"
                              value={applicant.host}
                              onChange={setApplicantField("host")}
                              placeholder="Optional"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Name of the Person filling the form instead of applicant</label>
                            <input
                              className="form-control booking-input text-uppercase"
                              value={applicant.formFillerName}
                              maxLength={51}
                              onChange={setApplicantField("formFillerName")}
                              placeholder="Optional"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Name Of Host Company Or Hotel</label>
                            <input
                              className="form-control booking-input text-uppercase"
                              value={applicant.hostCompanyOrHotel}
                              maxLength={101}
                              onChange={setApplicantField("hostCompanyOrHotel")}
                              placeholder="Optional"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Host Full Address</label>
                            <input
                              className="form-control booking-input text-uppercase"
                              value={applicant.hostFullAddress}
                              onChange={setApplicantField("hostFullAddress")}
                              placeholder="Optional"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Host Telephone</label>
                            <input
                              className="form-control booking-input"
                              value={applicant.hostTelephone}
                              minLength={7}
                              maxLength={12}
                              onChange={setApplicantField("hostTelephone")}
                              placeholder="7–12 digits"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Host Fax</label>
                            <input
                              className="form-control booking-input"
                              value={applicant.hostFax}
                              onChange={setApplicantField("hostFax")}
                              placeholder="Optional"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Host Email Address</label>
                            <input
                              type="email"
                              className="form-control booking-input"
                              value={applicant.hostEmail}
                              onChange={setApplicantField("hostEmail")}
                              placeholder="Optional"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Date Of Arrival in Schengen Area *</label>
                            <input
                              type="date"
                              className="form-control booking-input"
                              value={applicant.schengenArrivalDate}
                              onChange={setApplicantField("schengenArrivalDate")}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Date of Departure from Schengen Area *</label>
                            <input
                              type="date"
                              className="form-control booking-input"
                              value={applicant.schengenDepartureDate}
                              onChange={setApplicantField("schengenDepartureDate")}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Duration Of Stay *</label>
                            <input
                              className="form-control booking-input"
                              value={applicant.durationOfStay}
                              onChange={setApplicantField("durationOfStay")}
                              placeholder="e.g. 6"
                            />
                          </div>
                          <div className="col-md-12">
                            <label className="form-label">
                              Cost of travelling and living covered by *
                            </label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={COST_COVERED_BY_OPTIONS}
                              value={getApplicantMultiOptions("costCoveredBy", COST_COVERED_BY_OPTIONS)}
                              onChange={setApplicantMultiSelectField("costCoveredBy")}
                              isMulti
                              isClearable
                              isSearchable
                              placeholder="Select one or more"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Cost of travelling and living covered-Other</label>
                            <input
                              className="form-control booking-input text-uppercase"
                              value={applicant.costCoveredByOther}
                              maxLength={101}
                              onChange={setApplicantField("costCoveredByOther")}
                              placeholder="Optional"
                            />
                          </div>
                          <div className="col-md-12">
                            <label className="form-label">Applicant&apos;s Means of support? *</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={MEANS_OF_SUPPORT_OPTIONS}
                              value={getApplicantMultiOptions("meansOfSupport", MEANS_OF_SUPPORT_OPTIONS)}
                              onChange={setApplicantMultiSelectField("meansOfSupport")}
                              isMulti
                              isClearable
                              isSearchable
                              placeholder="Select one or more"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Applicant&apos;s Means of support-Other</label>
                            <input
                              className="form-control booking-input text-uppercase"
                              value={applicant.meansOfSupportOther}
                              maxLength={101}
                              onChange={setApplicantField("meansOfSupportOther")}
                              placeholder="Optional"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Schengen State of First Entry *</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={SCHENGEN_DESTINATION_OPTIONS}
                              value={getApplicantOption("schengenStateFirstEntry", SCHENGEN_DESTINATION_OPTIONS)}
                              onChange={setApplicantSelectField("schengenStateFirstEntry")}
                              isClearable
                              isSearchable
                              placeholder="Select Schengen state"
                            />
                          </div>
                          <div className="col-md-12">
                            <label className="form-label">Country (-ies) of destination *</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={SCHENGEN_DESTINATION_OPTIONS}
                              value={getApplicantMultiOptions("destinationCountries", SCHENGEN_DESTINATION_OPTIONS)}
                              onChange={setApplicantMultiSelectField("destinationCountries")}
                              isMulti
                              isClearable
                              isSearchable
                              placeholder="Select one or more countries"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Costs are covered by the inviting person or company *</label>
                            <Select
                              classNamePrefix="nationality-select"
                              options={YES_NO_OPTIONS}
                              value={getApplicantOption("costsCoveredByInvitingParty", YES_NO_OPTIONS)}
                              onChange={setApplicantSelectField("costsCoveredByInvitingParty")}
                              isClearable
                              placeholder="Select Yes or No"
                            />
                          </div>
                          {String(applicant.costsCoveredByInvitingParty || "").trim() === "No" && (
                            <div className="col-md-6">
                              <label className="form-label">Costs are covered by *</label>
                              <input
                                className="form-control booking-input text-uppercase"
                                value={applicant.costsCoveredBy}
                                maxLength={50}
                                onChange={setApplicantField("costsCoveredBy")}
                                placeholder="Required when No is selected"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    </div>

                    <div className="booking-form-actions">
                      {!job && isSwitzerlandFlow && (
                        <span className="booking-step-count" aria-live="polite">
                          Step <strong>{currentStep}</strong> of <strong>6</strong>
                        </span>
                      )}
                      {!job ? (
                        !isSwitzerlandFlow || currentStep === 1 ? (
                          <>
                            {isSwitzerlandFlow && (
                              <button
                                type="button"
                                className="btn btn-outline-primary booking-reset-btn"
                                onClick={handleNextStep}
                                disabled={loading}
                              >
                                Next: Passport Details
                              </button>
                            )}
                            <button
                              type="submit"
                              className="btn btn-primary booking-submit-btn"
                              disabled={loading}
                            >
                              <FiSend className="me-2" size={16} />
                              {loading ? "Sending..." : "Send to Extension"}
                            </button>
                          </>
                        ) : currentStep === 2 ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-outline-secondary booking-reset-btn"
                              onClick={handlePreviousStep}
                              disabled={loading}
                            >
                              Back
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary booking-submit-btn"
                              onClick={handleNextStep}
                            >
                              Next: Application Details
                            </button>
                          </>
                        ) : currentStep === 3 ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-outline-secondary booking-reset-btn"
                              onClick={handlePreviousStep}
                              disabled={loading}
                            >
                              Back
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary booking-submit-btn"
                              onClick={handleNextStep}
                            >
                              Next: Travel Details
                            </button>
                          </>
                        ) : currentStep === 4 ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-outline-secondary booking-reset-btn"
                              onClick={handlePreviousStep}
                              disabled={loading}
                            >
                              Back
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary booking-submit-btn"
                              onClick={handleNextStep}
                            >
                              Next: Accommodation Details
                            </button>
                          </>
                        ) : currentStep === 5 ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-outline-secondary booking-reset-btn"
                              onClick={handlePreviousStep}
                              disabled={loading}
                            >
                              Back
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary booking-submit-btn"
                              onClick={handleNextStep}
                            >
                              Next: Additional Details
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="btn btn-outline-secondary booking-reset-btn"
                              onClick={handlePreviousStep}
                              disabled={loading}
                            >
                              Back
                            </button>
                            <button
                              type="submit"
                              className="btn btn-primary booking-submit-btn"
                              disabled={loading}
                            >
                              <FiSend className="me-2" size={16} />
                              {loading ? "Sending..." : "Send to Extension"}
                            </button>
                          </>
                        )
                      ) : (
                        <button
                          type="button"
                          className="btn btn-outline-secondary booking-reset-btn"
                          onClick={handleReset}
                        >
                          Start New Booking
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              </section>
            </div>

            {job && (
            <div className="col-xl-4">
              <section className="booking-panel booking-status-card">
                <div className="booking-panel-body h-100">
                  <div className="booking-panel-head">
                    <div>
                      <h2 className="booking-panel-title">Job Status</h2>
                      <p className="booking-panel-subtitle">Live status.</p>
                    </div>
                    <span className="booking-panel-badge">
                      <FiActivity size={14} />
                      Live Tracking
                    </span>
                  </div>

                    <>
                      <div>
                        <span
                          className="booking-status-badge"
                          style={{ background: sc.bg, color: sc.color }}
                        >
                          <span className="booking-status-dot" />
                          {sc.label}
                        </span>
                        {["queued", "dispatched", "processing"].includes(liveStatus) && (
                          <div className="booking-refresh-note">Live update connected</div>
                        )}
                      </div>

                      <div className="booking-status-summary">
                        <div className="booking-status-stat">
                          <span className="booking-status-stat-label">Job Reference</span>
                          <span className="booking-status-stat-value">#{job.jobId}</span>
                        </div>
                        <div className="booking-status-stat">
                          <span className="booking-status-stat-label">Extension</span>
                          <span className="booking-status-stat-value">{connectionLabel}</span>
                        </div>
                        <div className="booking-status-stat">
                          <span className="booking-status-stat-label">Origin</span>
                          <span className="booking-status-stat-value">{originCountry?.name || job.originCode}</span>
                        </div>
                      </div>

                      <div className="booking-status-table">
                        <table className="table table-sm mb-0">
                          <tbody>
                            <tr>
                              <th>Origin</th>
                              <td>
                                <span className="badge bg-primary me-2">{originCountry?.iso_code || job.originCode}</span>
                                {originCountry?.name || "-"}
                              </td>
                            </tr>
                            <tr>
                              <th>Destination</th>
                              <td>
                                <span className="badge bg-success me-2">{destinationCountry?.iso_code || job.destinationCode}</span>
                                {destinationCountry?.name || "-"}
                              </td>
                            </tr>
                            <tr>
                              <th>Application Centre</th>
                              <td>{applicant.applicationCentre || "-"}</td>
                            </tr>
                            <tr>
                              <th>Appointment Category</th>
                              <td>{applicant.appointmentCategory || "-"}</td>
                            </tr>
                            <tr>
                              <th>Sub Category</th>
                              <td>{applicant.subCategory || "-"}</td>
                            </tr>
                            <tr>
                              <th>VFS Login URL</th>
                              <td>
                                <a
                                  href={job.loginUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="booking-status-link"
                                >
                                  {job.loginUrl}
                                </a>
                              </td>
                            </tr>
                            <tr>
                              <th>Extension Status</th>
                              <td>{connectionLabel}</td>
                            </tr>
                            {jobStatus?.result_msg && (
                              <tr>
                                <th>Last Update</th>
                                <td>{jobStatus.result_msg}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="booking-flow-note">Destination: {destinationCountry?.name || job.destinationCode}</div>
                    </>
                </div>
              </section>
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}